import { Component, OnInit, Inject } from '@angular/core';
import { MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { DomSanitizer, SafeResourceUrl } from '@angular/platform-browser';

interface Field {
  value: string;
  confidence?: string;
}

interface FieldsExtracted {
  [key: string]: Field;
}

interface POI_OCR_Data {
  module?: string;
  moduleId?: string;
  attempts?: number;
  countrySelected?: string;
  documentSelected?: string;
  expectedDocumentSide?: string;
  processedAt?: string;
  croppedImageUrl?: string;
  imageUrl?: string;
  apiResponseResult?: {
    details?: Array<{
      fieldsExtracted?: FieldsExtracted;
    }>;
  };
}

@Component({
  selector: 'app-pdfviewer',
  templateUrl: './pdfviewer.component.html',
  styleUrls: ['./pdfviewer.component.css']
})
export class PdfViewerComponent implements OnInit {
  public PdfData: any = {};
  poaOcrFrontSafeUrl: SafeResourceUrl | undefined;
  poaOcrBackSafeUrl: SafeResourceUrl | undefined;
  isPoaOcrFrontPdf: boolean = false;
  isPoaOcrBackPdf: boolean = false;
  isPoaOcrFrontImage: boolean = false;
  isPoaOcrBackImage: boolean = false;
  isPoiOcrImage: boolean = false;

  poiOcrSafeUrl: SafeResourceUrl | undefined;
  isPoiOcrPdf: boolean = false;
  livenessData: any;
  panDbCheck: any;
  fieldMatchPan: any;
  poaOcrFrontData: any;
  poaOcrBackData: any;
  faceMatchPoi: any;
  user_id: any;
  faceMatchPoaData: any;
  amlSearchApiData: any;
  isPdf: boolean = false;
  poiOcrData: POI_OCR_Data | undefined;
  fieldValues: { name: string, value: string }[] = [];

  constructor(
    @Inject(MAT_DIALOG_DATA) public data: any,
    private sanitizer: DomSanitizer,
    public dialogRef: MatDialogRef<PdfViewerComponent>
  ) {
    if (data) {
      this.PdfData = data || {};
    }
  }

  ngOnInit() {
    this.getApplicantDetails();
    this.getLiveness();
    this.getPOI_OCR();
    this.getPanDBCheck();
    this.getFieldMatchPan();
    this.getPOA_OCR_FRONT();
    this.getPOA_OCR_BACK();
    this.getFaceMatchPOI();
    this.getFaceMatchPOA();
    this.getAmlSearchApi();

    // Process URLs for both modules
    this.processUrls(this.poaOcrFrontData && this.poaOcrFrontData.imageUrl, 'poaOcrFront');
    this.processUrls(this.poaOcrBackData && this.poaOcrBackData.imageUrl, 'poaOcrBack');
    this.processUrls(this.poiOcrData && this.poiOcrData.imageUrl, 'poiOcr');
  }

  processUrls(url: string | undefined, moduleType: string): void {
    const imageUrl = url || '';

    // Check if the URL is a PDF or an image (jpg, png)
    const isPdf = imageUrl.endsWith('.pdf');
    const isImage = imageUrl.endsWith('.jpg') || imageUrl.endsWith('.png'); // Add other image types as needed

    // Sanitize the URL for safe binding in the template
    const safeUrl = this.sanitizeUrl(imageUrl);

    // Set properties based on module type
    if (moduleType === 'poaOcrFront') {
      this.poaOcrFrontSafeUrl = safeUrl;
      this.isPoaOcrFrontPdf = isPdf;
      this.isPoaOcrFrontImage = isImage; // Add flag for images
    } else if (moduleType === 'poaOcrBack') {
      this.poaOcrBackSafeUrl = safeUrl;
      this.isPoaOcrBackPdf = isPdf;
      this.isPoaOcrBackImage = isImage;
    } else if (moduleType === 'poiOcr') {
      this.poiOcrSafeUrl = safeUrl;
      this.isPoiOcrPdf = isPdf;
      this.isPoiOcrImage = isImage;
    }
  }
  getDetailKeys(details: any): string[] {
    return details ? Object.keys(details) : [];
  }
  getDisplayName(key: string): string {
    const keyMap: { [key: string]: string } = {
      liveFace: 'Live Face',
      multipleFaces: 'Multiple Faces',
      faceOccluded: 'Face Occluded',
      blur: 'Blur',
      eyesClosed: 'Eyes Closed',
      maskPresent: 'Mask Present'
    };

    return keyMap[key] || this.capitalizeFirstLetter(key);
  }
  capitalizeFirstLetter(str: string): string {
    return str.charAt(0).toUpperCase() + str.slice(1);
  }
  getKeys(obj: any): string[] {
    return Object.keys(obj);
  }
  objectKeys(obj: any): string[] {
    return Object.keys(obj);
  }
  getFlagEntries(flags: { [key: string]: string }): { key: string, value: string }[] {
    return Object.entries(flags).map(([key, value]) => ({ key, value }));
  }
  convertToCamelCaseWithSpaces = (text: any) => {
    if (!text) return '';

    return text
      .replace(/_/g, ' ')
      .replace(/\b\w/g, (char: any) => char.toUpperCase())
      .trim();
  }
  sanitizeUrl(url: string): SafeResourceUrl {
    return this.sanitizer.bypassSecurityTrustResourceUrl(url);
  }

  downloadFile(url: SafeResourceUrl | undefined, moduleName: any): void {
    if (!url) {
      console.error('Invalid URL');
      return;
    }

    const urlString = url as string;

    // Check the extension of the file to determine if it's an image or a PDF
    if (urlString.endsWith('.jpg') || urlString.endsWith('.png')) {
      // Handle image download
      const a = document.createElement('a');
      a.href = urlString;
      a.download = `${moduleName}.jpg`; // Save the image as JPG or PNG
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
    } else if (urlString.endsWith('.pdf')) {
      // Handle PDF download
      const a = document.createElement('a');
      a.href = urlString;
      a.download = `${moduleName}.pdf`; // Save the file as PDF
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
    } else {
      console.error('Unsupported file type');
    }
  }

  getLiveness(): void {
    if (this.PdfData && this.PdfData.result) {
      this.livenessData = this.PdfData.result.module_liveness || null;
    }
  }

  getPOI_OCR(): void {
    this.poiOcrData = (this.PdfData && this.PdfData.result && this.PdfData.result.module_POI_OCR) as POI_OCR_Data || null;

    const fieldsExtracted = this.poiOcrData && this.poiOcrData.apiResponseResult && this.poiOcrData.apiResponseResult.details && this.poiOcrData.apiResponseResult.details[0] && this.poiOcrData.apiResponseResult.details[0].fieldsExtracted;
    this.fieldValues = fieldsExtracted
      ? Object.entries(fieldsExtracted)
        .filter(([key, field]) => field && field.value)
        .map(([key, field]) => ({ name: key, value: field.value }))
      : [];
  }

  getPanDBCheck(): void {
    this.panDbCheck = (this.PdfData && this.PdfData.result && this.PdfData.result.module_panDBCheck) || null;
  }

  getFieldMatchPan(): void {
    this.fieldMatchPan = (this.PdfData && this.PdfData.result && this.PdfData.result.module_fieldMatchPan) || null;
  }

  getPOA_OCR_FRONT(): void {
    this.poaOcrFrontData = (this.PdfData && this.PdfData.result && this.PdfData.result.module_POA_OCR_FRONT) || null;
  }

  getPOA_OCR_BACK(): void {
    this.poaOcrBackData = (this.PdfData && this.PdfData.result && this.PdfData.result.module_POA_OCR_BACK) || null;
  }

  getFaceMatchPOI(): void {
    this.faceMatchPoi = (this.PdfData && this.PdfData.result && this.PdfData.result.module_faceMatchPOI) || null;
  }

  getFaceMatchPOA(): void {
    this.faceMatchPoaData = (this.PdfData && this.PdfData.result && this.PdfData.result.module_faceMatchPOA) || null;
  }

  getAmlSearchApi(): void {
    this.amlSearchApiData = (this.PdfData && this.PdfData.result && this.PdfData.result.module_aml_search_api) || null;
  }

  getApplicantDetails(): void {
    this.user_id = this.PdfData && this.PdfData.metadata && this.PdfData.metadata.transactionId;
  }

  printContent(): void {
    const printWindow = window.open('', '', 'height=600,width=800');
    const contentElement = document.querySelector('.module-section');
    const content = contentElement ? contentElement.innerHTML : '';

    if (printWindow) {
      printWindow.document.write('<html><head><title>Print</title></head><body>');
      printWindow.document.write(content);
      printWindow.document.write('</body></html>');
      printWindow.document.close();
      printWindow.focus();
      printWindow.print();
    }
  }


  getDetailsEntries(details: any): any[] {
    return Object.entries(details).map(([key, value]) => ({ key, value }));
  }
}



// WEBPACK FOOTER //
// ./src/app/pdfviewer/pdfviewer.component.ts